import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../api/api';
import { PurchaseList } from '../../types';
import { extractApiErrorMessage } from '../../utils/apiError';
import { getOperatingContext, isOperatingContextReady } from '../../utils/operatingContextLifecycle';

interface PurchaseState {
  purchaseList: PurchaseList | null;
  loading: boolean;
  error: string | null;
  activeRequestId: string | null;
}

export const fetchPurchaseList = createAsyncThunk(
  'purchase/fetchList',
  async (params: { from?: string; to?: string } | undefined, { rejectWithValue, signal }) => {
    const context = getOperatingContext();
    if (!isOperatingContextReady(context, localStorage.getItem('token')))
      return rejectWithValue(context.error || 'Робочу групу ще не підтверджено.');
    try {
      const query = new URLSearchParams();
      if (params?.from) query.append('from', params.from);
      if (params?.to) query.append('to', params.to);
      const res = await api.get<PurchaseList>(`/purchases/list?${query.toString()}`, { signal });
      return res.data;
    } catch (error) {
      return rejectWithValue(extractApiErrorMessage(error, 'Не вдалося сформувати список закупівель.'));
    }
  }
);

const purchaseSlice = createSlice({
  name: 'purchase',
  initialState: { purchaseList: null, loading: false, error: null, activeRequestId: null } as PurchaseState,
  reducers: {
    invalidatePurchaseList(state) {
      state.purchaseList = null;
      state.loading = false;
      state.error = null;
      state.activeRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchaseList.pending, (state, action) => {
        state.activeRequestId = action.meta.requestId;
        state.purchaseList = null;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseList.fulfilled, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) return;
        state.activeRequestId = null;
        state.purchaseList = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchPurchaseList.rejected, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) return;
        state.activeRequestId = null;
        state.purchaseList = null;
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Не вдалося сформувати список закупівель.';
      });
  },
});

export const { invalidatePurchaseList } = purchaseSlice.actions;
export default purchaseSlice.reducer;
